import { FormattedMessage, useIntl } from "react-intl";
import type { UseMatchEditorResult } from "./use-match-editor";
import { userErrorMessage } from "@/lib/user-error";
import { Button } from "@/components/design/Button";
import { incompleteConditionCount } from "./match-design-rules";

interface ReadinessItem {
  label: string;
  ok: boolean;
  hint?: string;
}

// Panel copy per immutable mode (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@1). Keyed on the precise matchMode, not the
// assessOnly flag — that flag conflates Assess and Rank, which need distinct copy.
// One table beats three parallel ternary cascades that must be kept in sync.
const MODE_COPY = {
  OPTIMIZE: {
    eyebrow: { id: "match.preSolve.eyebrow", defaultMessage: "Bereit zum Solven" },
    heading: { id: "match.preSolve.heading", defaultMessage: "Aus deinem Kandidatenpool ein optimales Team komponieren." },
    running: { id: "match.preSolve.solverRunning", defaultMessage: "Solver läuft…" },
    idle: { id: "match.preSolve.startSolver", defaultMessage: "Solver starten" },
  },
  ASSESS: {
    eyebrow: { id: "match.preSolve.eyebrowAssess", defaultMessage: "Bereit zur Bewertung" },
    heading: { id: "match.preSolve.headingAssess", defaultMessage: "Bewerte, wie gut dein Team die Anforderungen erfüllt." },
    running: { id: "match.preSolve.assessing", defaultMessage: "Bewerte Team…" },
    idle: { id: "match.preSolve.assessTeam", defaultMessage: "Team bewerten" },
  },
  RANK: {
    eyebrow: { id: "match.preSolve.eyebrowRank", defaultMessage: "Bereit zum Ranking" },
    heading: { id: "match.preSolve.headingRank", defaultMessage: "Sortiere deine Kandidaten nach Passung zum Anforderungsprofil." },
    running: { id: "match.preSolve.ranking", defaultMessage: "Ranke…" },
    idle: { id: "match.preSolve.rankCandidates", defaultMessage: "Kandidaten ranken" },
  },
} as const;

export function PreSolveState({ editor }: { editor: UseMatchEditorResult }) {
  const intl = useIntl();
  const teamSize = editor.data.teamSize ?? 0;
  const candidates = editor.candidates.length;
  const lockedCount = editor.candidates.filter((c) => c.locked).length;
  const assess = editor.assessOnly;
  const matchMode = editor.data.matchMode ?? "OPTIMIZE";
  const copy = MODE_COPY[matchMode as keyof typeof MODE_COPY] ?? MODE_COPY.OPTIMIZE;
  const eyebrow = intl.formatMessage(copy.eyebrow);
  const heading = intl.formatMessage(copy.heading);
  const buttonLabel = intl.formatMessage(editor.solverLoading ? copy.running : copy.idle);
  // Optimize handles locked candidates as a fixed in-team set (#150). More locks than
  // seats can never solve → hard-block pre-run and steer to unlock / raise team size.
  // Exactly-full (== teamSize) is allowed through: genesis renders the degenerate
  // evaluation of the fully-locked team. Assess evaluates a given team, so the seat
  // arithmetic doesn't apply there.
  // ponytail: seats == teamSize because teamCount is always 1 today (see match-design-mapping.ts).
  // agent-service SolveGuard.kt mirrors this exact check and MUST stay identical. When multi-team
  // ships, total seats become teamSize * teamCount here, in SolveGuard.kt, and in the genesis
  // solver — change all three together or valid multi-team solves get falsely blocked.
  const lockedExceedsTeam = !assess && teamSize > 0 && lockedCount > teamSize;
  const fullyLocked = !assess && teamSize > 0 && lockedCount === teamSize;

  // Assess/Rank (assessOnly) have no team to size (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@1): Assess evaluates a
  // given team, Rank scores candidates independently. The team-size readiness row
  // and the seat arithmetic only apply to Optimize.
  const items: ReadinessItem[] = [];
  if (!assess) {
    items.push({
      label: intl.formatMessage({
        id: "match.preSolve.teamSizeSet",
        defaultMessage: "Team-Größe gesetzt",
      }),
      ok: teamSize > 0,
      hint:
        teamSize > 0
          ? intl.formatMessage(
              {
                id: "match.preSolve.personsCount",
                defaultMessage: "{n} Personen",
              },
              { n: teamSize },
            )
          : intl.formatMessage({
              id: "match.preSolve.noSizeYet",
              defaultMessage: "noch keine Größe gewählt",
            }),
    });
  }
  items.push({
    label: intl.formatMessage({
      id: "match.preSolve.candidatesLoaded",
      defaultMessage: "Kandidaten geladen",
    }),
    ok: assess ? candidates > 0 : candidates >= teamSize && teamSize > 0,
    hint: assess
      ? intl.formatMessage({ id: "match.preSolve.poolOnly", defaultMessage: "{n} im Pool" }, { n: candidates })
      : teamSize > 0
        ? intl.formatMessage(
            {
              id: "match.preSolve.poolWithLocked",
              defaultMessage: "{n} im Pool · {locked} fix",
            },
            { n: candidates, locked: lockedCount },
          )
        : intl.formatMessage(
            {
              id: "match.preSolve.poolOnly",
              defaultMessage: "{n} im Pool",
            },
            { n: candidates },
          ),
  });
  // Unvollstaendige Kriterien (z.B. Branche ohne Auswahl) sind ein hartes Gate in BEIDEN
  // Modi: genesis wirft sonst auf leere UUIDs. Der assess-Bypass gilt nur fuer die
  // Team-Checkliste oben.
  const incomplete = incompleteConditionCount(editor.data);
  if (incomplete > 0) {
    items.push({
      label: intl.formatMessage({
        id: "match.preSolve.conditionsComplete",
        defaultMessage: "Kriterien vollständig",
      }),
      ok: false,
      hint: intl.formatMessage(
        {
          id: "match.preSolve.conditionsIncompleteHint",
          defaultMessage:
            "{n, plural, one {# Kriterium ohne Auswahl (z. B. Branche wählen) – ausfüllen oder entfernen} other {# Kriterien ohne Auswahl – ausfüllen oder entfernen}}",
        },
        { n: incomplete },
      ),
    });
  }
  // Every readiness row must pass. The Optimize-only "Team-Größe gesetzt" row is already excluded
  // from `items` in assess mode (see the `if (!assess)` guard above), and the "Kandidaten geladen"
  // row is itself mode-aware, so Assess/Rank still require a non-empty pool here.
  const ready =
    items.every((i) => i.ok) &&
    incomplete === 0 &&
    !lockedExceedsTeam &&
    !editor.solverLoading;

  return (
    <div className="box bg-surface px-8 py-5 @lg:px-12 @lg:py-6">
      <div className="text-[10px] tracking-[0.25em] text-ink/55">{eyebrow}</div>
      <h3 className="mt-3 font-serif text-2xl leading-tight">{heading}</h3>
      {/* The seat-count intro is Optimize-only (it names team-size slots). Assess/Rank
          get no substitute copy here — their panel framing is #21's UX work. */}
      {!assess && (
        <p className="mt-2 max-w-lg text-sm text-ink/55">
          <FormattedMessage
            id="match.preSolve.intro"
            defaultMessage="Der Solver verteilt deine Kandidaten auf {teamSize} Plätze und optimiert auf dein Anforderungsprofil. Du kannst die Berechnung beliebig oft wiederholen."
            values={{
              teamSize:
                teamSize ||
                intl.formatMessage({
                  id: "match.preSolve.teamSizePlaceholder",
                  defaultMessage: "n",
                }),
            }}
          />
        </p>
      )}

      <ul className="mt-4 space-y-1.5">
        {items.map((it) => (
          <li key={it.label} className="flex items-baseline gap-3">
            <span
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] ${
                it.ok ? "bg-emerald-500/15 text-emerald-700" : "bg-ink/[0.06] text-ink/45"
              }`}
              aria-hidden
            >
              {it.ok ? "✓" : "○"}
            </span>
            <div className="flex-1">
              <div className="text-[13px]">{it.label}</div>
              {it.hint && <div className="text-[11px] text-ink/55">{it.hint}</div>}
            </div>
          </li>
        ))}
      </ul>

      {lockedExceedsTeam && (
        <div
          role="status"
          className="mt-4 grid grid-cols-[auto_minmax(0,1fr)] gap-3 rounded-md bg-accent/[0.08] px-4 py-3"
          data-testid="locked-exceeds-team"
        >
          <span
            className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent/15 text-[10px] font-semibold text-accent"
            aria-hidden
          >
            !
          </span>
          <div>
            <div className="text-[12px] font-medium text-ink">
              <FormattedMessage
                id="match.preSolve.lockedExceedsHeading"
                defaultMessage="More people are locked than there are seats."
              />
            </div>
            <div className="text-[11px] leading-snug text-ink/55">
              <FormattedMessage
                id="match.preSolve.lockedExceedsBody"
                defaultMessage="{locked} people are locked in, but the team has {teamSize} seats. Unlock some members or raise the team size before solving."
                values={{ locked: lockedCount, teamSize }}
              />
            </div>
          </div>
        </div>
      )}
      {fullyLocked && (
        <p className="mt-4 text-[11px] text-ink/55" data-testid="fully-locked-hint">
          <FormattedMessage
            id="match.preSolve.fullyLockedHint"
            defaultMessage="All {teamSize} seats are filled by locked members. The Solver will only evaluate this team, not optimize it."
            values={{ teamSize }}
          />
        </p>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          size="lg"
          data-testid="solver-start-button"
          onClick={() => void editor.runSolver()}
          disabled={!ready}
        >
          {buttonLabel}
        </Button>
        {editor.solverError && (
          <span className="text-[11px] text-rose-600">
            <FormattedMessage
              id="common.errorWithMessage"
              defaultMessage="Fehler: {message}"
              values={{ message: userErrorMessage(intl, editor.solverError) }}
            />
          </span>
        )}
        {!ready && !editor.solverLoading && (
          <span className="text-[11px] text-ink/45">
            <FormattedMessage
              id="match.preSolve.fulfillFirst"
              defaultMessage="Erfülle zuerst die Anforderungen oben."
            />
          </span>
        )}
      </div>
    </div>
  );
}
